import { useRef, useEffect, useState, useMemo } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useLocation, useNavigate } from 'react-router-dom';
import Link from 'shared/SlashLink';
import blogs from '../../api/blogs';
import arrow from '../../images/shapes/shape_arrow_right.svg';
import arrow2 from '../../images/shapes/shape_arrow_left.svg';
import icon1 from '../../images/icons/icon_calendar.svg';
import BlogSidebar from '../BlogSidebar';

const PAGE_SIZE = 6;

const sortedBlogs = [...blogs].sort((a, b) => new Date(b.date) - new Date(a.date));
const featuredBlogs = sortedBlogs.filter((b) => b.featured).slice(0, 3);
const featuredSlice = featuredBlogs.length ? featuredBlogs : sortedBlogs.slice(0, 3);

const BlogList = () => {
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const swiperRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    const params = new URLSearchParams(location.search);
    const activeTag = params.get('tag') || '';
    const activeQuery = params.get('q') || '';
    const activeCat = params.get('cat') || '';
    const pageParam = parseInt(params.get('page') || '1', 10);

    const [page, setPage] = useState(pageParam);
    useEffect(() => { setPage(pageParam); }, [pageParam]);

    useEffect(() => {
        if (swiperRef.current && prevRef.current && nextRef.current) {
            swiperRef.current.params.navigation.prevEl = prevRef.current;
            swiperRef.current.params.navigation.nextEl = nextRef.current;
            swiperRef.current.navigation.init();
            swiperRef.current.navigation.update();
        }
    }, []);

    const ClickHandler = () => window.scrollTo(10, 0);

    const filtered = useMemo(() => {
        const q = activeQuery.trim().toLowerCase();
        return sortedBlogs.filter((b) => {
            if (activeTag && !(b.tags || []).includes(activeTag)) return false;
            if (activeCat && b.category !== activeCat) return false;
            if (q) {
                const hay = `${b.title} ${b.excerpt} ${(b.tags || []).join(' ')} ${b.category}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [activeTag, activeQuery, activeCat]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const pagedBlogs = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    const updateQuery = (updates) => {
        const next = new URLSearchParams(location.search);
        Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === '' || v === undefined) next.delete(k);
            else next.set(k, v);
        });
        navigate(`/blog?${next.toString()}`);
        window.scrollTo({ top: 200, behavior: 'smooth' });
    };

    const goToPage = (n) => updateQuery({ page: n === 1 ? null : String(n) });
    const clearFilters = () => navigate('/blog');

    const activeFilterChips = [
        activeTag && { label: `Tag: ${activeTag}`, key: 'tag' },
        activeCat && { label: `Category: ${activeCat}`, key: 'cat' },
        activeQuery && { label: `Search: "${activeQuery}"`, key: 'q' },
    ].filter(Boolean);

    return (
        <section className="blog_section section_space bg-light">
            <div className="container">
                {activeFilterChips.length === 0 && (
                    <div className="blog_onecol_carousel overflow-hidden">
                            <Swiper
                                modules={[Navigation]}
                                spaceBetween={50}
                                slidesPerView={1}
                                loop={featuredSlice.length > 1}
                                speed={1800}
                                ref={swiperRef}
                                onBeforeInit={(swiper) => { swiperRef.current = swiper; }}
                            >
                                {featuredSlice.map((blog) => (
                                    <SwiperSlide key={blog.slug}>
                                        <div className="blog_post_block content_over_layout">
                                            <div className="blog_post_image">
                                                <Link onClick={ClickHandler} to={`/blog-single/${blog.slug}`} className="image_wrap">
                                                    <img src={blog.bSingle} alt={blog.title} loading="lazy" />
                                                </Link>
                                            </div>
                                            <div className="blog_post_content">
                                                <div className="post_meta_wrap">
                                                    <ul className="category_btns_group unordered_list">
                                                        <li><Link onClick={ClickHandler} to={`/blog?cat=${encodeURIComponent(blog.category)}`}>{blog.category}</Link></li>
                                                        <li><Link onClick={ClickHandler} to={`/blog?tag=${encodeURIComponent(blog.tags?.[0] || '')}`}>{blog.thumb}</Link></li>
                                                    </ul>
                                                    <ul className="post_meta unordered_list">
                                                        <li>
                                                            <Link onClick={ClickHandler} to={`/blog-single/${blog.slug}`}>
                                                                <i className="fa-regular fa-calendar-days" aria-hidden="true"></i> {blog.create_at}
                                                            </Link>
                                                        </li>
                                                        <li>
                                                            <Link onClick={ClickHandler} to={`/blog-single/${blog.slug}`}>
                                                                <i className="fa-regular fa-clock" aria-hidden="true"></i> {blog.readTime} min read
                                                            </Link>
                                                        </li>
                                                    </ul>
                                                </div>
                                                <h3 className="blog_post_title">
                                                    <Link onClick={ClickHandler} to={`/blog-single/${blog.slug}`}>
                                                        {blog.title}
                                                    </Link>
                                                </h3>
                                                <p className="mb-0">{blog.excerpt}</p>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        <button ref={prevRef} className="b1cc-swiper-button-prev" type="button" style={{ backgroundImage: `url(${arrow})` }}>
                            <i className="fa-solid fa-angles-left" aria-hidden="true"></i>
                        </button>
                        <button ref={nextRef} className="b1cc-swiper-button-next" type="button" style={{ backgroundImage: `url(${arrow2})` }}>
                            <i className="fa-solid fa-angles-right" aria-hidden="true"></i>
                        </button>
                        <div className="b1cc-swiper-pagination p-0"></div>
                    </div>
                )}

                <div className={activeFilterChips.length === 0 ? 'pt-5' : ''}>
                    <div className="row">
                        <div className="col-lg-8">
                            {activeFilterChips.length > 0 && (
                                <div className="mb-4 d-flex flex-wrap align-items-center" style={{ gap: 10 }}>
                                    <span style={{ fontWeight: 600, color: 'var(--bs-dark)' }}>Filtering by:</span>
                                    {activeFilterChips.map((c) => (
                                        <span key={c.key} style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 6,
                                            padding: '5px 12px', borderRadius: '20px',
                                            background: 'var(--bs-primary-bg-subtle)', color: 'var(--bs-primary)',
                                            fontSize: '.85rem', fontWeight: 600,
                                        }}>
                                            {c.label}
                                        </span>
                                    ))}
                                    <button onClick={clearFilters} className="btn btn-sm btn-outline-secondary" style={{ marginLeft: 8 }}>
                                        Clear all
                                    </button>
                                    <span style={{ color: 'var(--bs-body-color)', fontSize: '.9rem', marginLeft: 'auto' }}>
                                        {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
                                    </span>
                                </div>
                            )}

                            {pagedBlogs.length === 0 ? (
                                <div className="text-center py-5" style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--bs-border-color)' }}>
                                    <h3 style={{ color: 'var(--bs-dark)' }}>No posts found</h3>
                                    <p>Try a different search or <button onClick={clearFilters} className="btn btn-link p-0">clear all filters</button>.</p>
                                </div>
                            ) : (
                                pagedBlogs.map((blog) => (
                                    <div className="blog_post_block image_left_layout" key={blog.slug}>
                                        <div className="blog_post_image">
                                            <Link onClick={ClickHandler} to={`/blog-single/${blog.slug}`} className="image_wrap">
                                                <img src={blog.screens} alt={blog.title} loading="lazy" />
                                            </Link>
                                        </div>
                                        <div className="blog_post_content">
                                            <div className="post_meta_wrap">
                                                <ul className="category_btns_group unordered_list">
                                                    <li><Link onClick={ClickHandler} to={`/blog?cat=${encodeURIComponent(blog.category)}`}>{blog.category}</Link></li>
                                                </ul>
                                                <ul className="post_meta unordered_list">
                                                    <li>
                                                        <Link onClick={ClickHandler} to={`/blog-single/${blog.slug}`}>
                                                            <img src={icon1} alt="Calendar" /> {blog.create_at}
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link onClick={ClickHandler} to={`/blog-single/${blog.slug}`}>
                                                            <i className="fa-regular fa-clock" aria-hidden="true"></i> {blog.readTime} min read
                                                        </Link>
                                                    </li>
                                                </ul>
                                            </div>
                                            <h3 className="blog_post_title">
                                                <Link onClick={ClickHandler} to={`/blog-single/${blog.slug}`}>{blog.title}</Link>
                                            </h3>
                                            <p>{blog.excerpt}</p>
                                            <Link onClick={ClickHandler} to={`/blog-single/${blog.slug}`} className="btn btn-dark">
                                                <span className="btn_label" data-text="Read More">Read More</span>
                                                <span className="btn_icon"><i className="fa-solid fa-arrow-up-right" aria-hidden="true"></i></span>
                                            </Link>
                                        </div>
                                    </div>
                                ))
                            )}

                            {totalPages > 1 && (
                                <div className="pagination_wrap pb-0">
                                    <ul className="pagination_nav unordered_list justify-content-center">
                                        <li className={safePage === 1 ? 'disabled' : ''}>
                                            <button type="button" onClick={() => safePage > 1 && goToPage(safePage - 1)} style={{ background: 'transparent', border: 'none', color: 'inherit' }}>
                                                <i className="fa-solid fa-angles-left" aria-hidden="true"></i>
                                            </button>
                                        </li>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                                            <li key={n} className={n === safePage ? 'active' : ''}>
                                                <button type="button" onClick={() => goToPage(n)} style={{ background: 'transparent', border: 'none', color: 'inherit' }}>
                                                    {n}
                                                </button>
                                            </li>
                                        ))}
                                        <li className={safePage === totalPages ? 'disabled' : ''}>
                                            <button type="button" onClick={() => safePage < totalPages && goToPage(safePage + 1)} style={{ background: 'transparent', border: 'none', color: 'inherit' }}>
                                                <i className="fa-solid fa-angles-right" aria-hidden="true"></i>
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>
                        <BlogSidebar />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BlogList;
